import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useIntl } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';
import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TableSortLabel,
  TablePagination,
  Paper,
  Typography,
  Button,
} from '@mui/material';
import { Loading, Tooltip } from '@gpstrackit/gtc-react-controls';
import { TagsGroup } from 'Components/Tag';
import {
  FileDownload as ExportIcon,
  Visibility as ViewIcon,
} from '@mui/icons-material';
import { getTagColor } from 'Core/data/Helpers';
import {
  fetchAssetsWithOpenDefects,
  fetchAssetTypes,
  searchAssets,
  searchAssetDefectsQuery,
  buildAssetDefectsSearchVariables,
} from '../redux/slice';
import FilterPanel from '../components/filterPanel';
import StatusIcon from '../components/statusIcon';
import { FEATURE_FLAGS } from '../featureFlags';
import { NotificationManager } from 'Components/Notifications';
import { sendGenericExport, clearRedux } from 'Redux/actions';
import { useTagsManage } from 'Modules/.common/components/TagsManage/useTagsManage';

const EXPORT_NAME = 'assetsWithOpenDefects';

const AssetsOpenDefects = ({ onViewDefects }) => {
  const { messages } = useIntl();
  const dispatch = useDispatch();

  // Access state directly in the UI
  const data = useSelector((state) => state.defectsGtcRedux?.assetsWithDefects);
  const totalRecords = useSelector((state) => state.defectsGtcRedux?.assetsWithDefectsTotal || 0);
  const loading = useSelector((state) => state.defectsGtcRedux?.assetsWithDefectsLoading || false);
  const assetTypes = useSelector((state) => state.defectsGtcRedux?.assetTypes || []);
  const assetSearchResults = useSelector((state) => state.defectsGtcRedux?.assetSearchResults || []);
  const assetSearchLoading = useSelector((state) => state.defectsGtcRedux?.assetSearchLoading || false);
  const exportResult = useSelector((state) => state.commomRedux.exportResult);
  const exportName = useSelector((state) => state.commomRedux.exportName);
  const loadingExport = useSelector((state) => state.commomRedux.loadingExport);
  const { tags: tagTreeData = [] } = useTagsManage({ onlyTags: true });

  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(20);
  const [orderBy, setOrderBy] = useState('opstat');
  const [order, setOrder] = useState('desc');
  const [filters, setFilters] = useState({});

  // Load filter options on mount
  useEffect(() => {
    dispatch(fetchAssetTypes());
  }, [dispatch]);

  const tagLabelLookup = useMemo(() => {
    const map = new Map();
    (tagTreeData || []).forEach((tag) => {
      if (tag?.id === undefined || !tag?.label) {
        return;
      }
      map.set(String(tag.id), tag.label);
    });
    return map;
  }, [tagTreeData]);

  // Build filterOptions from Redux + tag tree
  const filterOptions = useMemo(() => {
    const options = {
      assetTypes,
      defectStatuses: true,
    };

    if (tagTreeData?.length) {
      options.tags = tagTreeData;
    }

    return options;
  }, [assetTypes, tagTreeData]);

  // Load data when filters, page, or sort changes
  const loadData = useCallback(() => {
    dispatch(fetchAssetsWithOpenDefects({
      ...filters,
      page: page + 1,
      limit: rowsPerPage,
      sortField: orderBy,
      sortDirection: order,
    }));
  }, [dispatch, filters, page, rowsPerPage, orderBy, order]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  useEffect(() => {
    if (exportName !== EXPORT_NAME || exportResult === null) {
      return;
    }

    if (exportResult) {
      NotificationManager.info(
        messages['exportInProcessBody'],
        messages['exportInProcessTitle'],
        4000,
        null,
        null,
        'info',
        null,
      );
    } else {
      NotificationManager.error(
        messages['exportError'] || 'Unable to export CSV. Please try again.',
        messages['error'] || 'Error',
        4000,
        null,
        null,
        'error',
      );
    }
    dispatch(clearRedux('COMMON', { exportResult: null, exportName: null }));
  }, [dispatch, exportResult, exportName, messages]);

  const handleSort = (field) => {
    const isAsc = orderBy === field && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(field);
  };

  const handleFilterChange = (newFilters) => {
    setFilters(newFilters);
    setPage(0);
  };

  const handleClearFilters = () => {
    setFilters({});
    setPage(0);
  };

  const getActiveFilters = () => {
    const active = [];
    // Handle multiple locations - create a chip for each selected location
    if (filters.locations && filters.locations.length > 0) {
      filters.locations.forEach((location) => {
        active.push({
          field: 'locationIds',
          filterKey: `locationIds-${location.id}`,
          label: 'Location',
          value: location.name || location.id,
          removeValue: location.id,
        });
      });
    }
    // Handle multiple asset types - create a chip for each selected type
    if (filters.assetTypeIds && filters.assetTypeIds.length > 0) {
      filters.assetTypeIds.forEach((typeId) => {
        const type = filterOptions.assetTypes?.find((t) => t.id === typeId);
        active.push({
          field: 'assetTypeIds',
          filterKey: `assetTypeIds-${typeId}`,
          label: 'Asset Type',
          value: type?.name || typeId,
          removeValue: typeId,
        });
      });
    }
    // Handle multiple assets - create a chip for each selected asset
    if (filters.assets && filters.assets.length > 0) {
      filters.assets.forEach((asset) => {
        active.push({
          field: 'assetIds',
          filterKey: `assetIds-${asset.id}`,
          label: 'Asset',
          value: asset.fleet || asset.id,
          removeValue: asset.id,
        });
      });
    }
    if (filters.tagIds && filters.tagIds.length > 0) {
      filters.tagIds.forEach((tagId) => {
        const tagLabel = tagLabelLookup.get(String(tagId));
        active.push({
          field: 'tagIds',
          filterKey: `tagIds-${tagId}`,
          label: 'Tag',
          value: tagLabel || tagId,
          removeValue: tagId,
        });
      });
    }
    if (FEATURE_FLAGS.ENABLE_GLOBAL_COMPONENT_FILTER && filters.componentId) {
      // Use the stored component name from filters (set by FilterPanel onChange)
      active.push({ field: 'componentId', label: 'Component', value: filters.componentName || filters.componentId });
    }
    // Include defectStatus when not the default "Show All" (-8)
    if (filters.defectStatus && filters.defectStatus !== -8) {
      const statusLabels = { '-10': 'Open', '-6': 'Pending' };
      active.push({
        field: 'defectStatus',
        filterKey: 'defectStatus',
        label: 'Defect Status',
        value: statusLabels[String(filters.defectStatus)] || filters.defectStatus,
      });
    }
    return active;
  };

  const formatDate = (timestamp) => {
    if (!timestamp) return 'N/A';
    return new Date(timestamp).toLocaleString();
  };

  // Search callback for FilterPanel - dispatches action, results come via Redux state
  const handleSearchAssets = useCallback((query) => {
    if (!query || query.trim().length === 0) return;
    dispatch(searchAssets({ query }));
  }, [dispatch]);

  // Placeholder to avoid errors
  // Need to implement when feature flag is enabled
  const searchComponents = () => { };

  const onExport = useCallback(() => {
    const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
    const headers = [
      {
        header: messages['asset'],
        column: 'assetLabel',
        functionString: 'return row?.assetLabel',
      },
      {
        header: messages['type'],
        column: 'assetTypeLabel',
        functionString: 'return row?.assetTypeLabel',
      },
      {
        header: messages['manufacturer'],
        column: 'make',
        functionString: 'return row?.make',
      },
      {
        header: messages['vin'],
        column: 'vin',
        functionString: 'return row?.vin',
      },
      {
        header: messages['lastInspection'],
        column: 'lastInspectionStartTime',
        functionString: `return row?.lastInspectionStartTime?(typeof moment.tz==='function'?moment.tz(row.lastInspectionStartTime,'${tz}'):moment(row.lastInspectionStartTime)).format('MM/DD/YYYY hh:mm:ss A'):'';`,
      },
      {
        header: messages['severity'],
        column: 'severity',
        functionString: `
          const STATUS_LABELS = {
            0: '${messages['noDefects'] || 'No Defects'}',
            127: '${messages['warning'] || 'Warning'}',
            255: '${messages['criticalDefects'] || 'Critical Defects'}',
          };
          return STATUS_LABELS[row?.status] || STATUS_LABELS[0];
        `,
      },
      {
        header: messages['defects'],
        column: 'numberOfDefects',
        functionString: 'return row?.numberOfDefects || 0',
      },
    ];

    dispatch(
      sendGenericExport(
        EXPORT_NAME,
        'Csv',
        searchAssetDefectsQuery,
        buildAssetDefectsSearchVariables({
          ...filters,
          sortField: orderBy,
          sortDirection: order,
          page: 1,
          limit: totalRecords,
        }),
        headers,
        { unit: 'px', size: 'A4', orientation: 'landscape', fontSize: 10 },
        [],
        false,
        true,
      )
    );
  }, [dispatch, filters, orderBy, order, totalRecords, messages]);

  const columns = [
    { id: 'fleet', label: messages.asset || 'Asset', sortable: true },
    { id: 'tags', label: messages.tags || 'Tags', sortable: false },
    { id: 'assetType', label: messages.type || 'Type', sortable: true },
    { id: 'manufacturer', label: messages.manufacturer || 'Manufacturer', sortable: true },
    { id: 'vin', label: messages.vin || 'VIN', sortable: false },
    { id: 'inspDate', label: messages.lastInspection || 'Last Inspection', sortable: true },
    { id: 'opstat', label: messages.severity || 'Severity', sortable: true },
    { id: 'openDefectCount', label: messages.defects || 'Defects', sortable: true },
    { id: 'actions', label: messages.actions || 'Actions', sortable: false },
  ];

  // Show full-page loading on initial load
  if (loading && data === null) {
    return <Loading full />;
  }

  return (
    <Box>
      <Box className="page-header">
        <Box className="title-section">
          <Typography className="page-title">{messages.assetsWithOpenDefects || "Assets with Open Defects"}</Typography>
          <span className="record-count">{totalRecords} {messages.assets || "assets"}</span>
        </Box>
        <Box className="action-buttons">
          <Button
            variant="outlined"
            startIcon={<ExportIcon />}
            size="small"
            onClick={onExport}
            disabled={loading || loadingExport}>
            {messages.exportCSV || "Export CSV"}
          </Button>
        </Box>
      </Box>

      <FilterPanel
        filters={filters}
        filterOptions={filterOptions}
        onFilterChange={handleFilterChange}
        onClearFilters={handleClearFilters}
        activeFilters={getActiveFilters()}
        // Temporarily hide the location filter until the GraphQL SearchAssetDefects query supports searching by locationId.
        // This will be completed in ticket EVIRPX-425.
        // onSearchLocations={searchLocations}
        onSearchAssets={handleSearchAssets}
        assetSearchResults={assetSearchResults}
        assetSearchLoading={assetSearchLoading}
        onSearchComponents={FEATURE_FLAGS.ENABLE_GLOBAL_COMPONENT_FILTER ? searchComponents : undefined}
      />

      <Paper className="data-table">
        <div className="defects-table-container">
          <Table stickyHeader size="small">
            <TableHead>
              <TableRow>
                {columns.map((column) => (
                  <TableCell key={column.id}>
                    {column.sortable ? (
                      <TableSortLabel
                        active={orderBy === column.id}
                        direction={orderBy === column.id ? order : 'asc'}
                        onClick={() => handleSort(column.id)}
                      >
                        {column.label}
                      </TableSortLabel>
                    ) : (
                      column.label
                    )}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={columns.length} align="center" sx={{ py: 4 }}>
                    <Loading size={32} />
                  </TableCell>
                </TableRow>
              ) : (data || []).length === 0 ? (
                <TableRow>
                  <TableCell colSpan={columns.length}>
                    <Box className="no-results">
                      <Typography variant="h6">{messages.noAssetsWithOpenDefects || "No assets with open defects"}</Typography>
                      <Typography variant="body2" color="textSecondary">
                        {messages.tryAdjustingFilters || "Try adjusting your filters"}
                      </Typography>
                    </Box>
                  </TableCell>
                </TableRow>
              ) : (
                data.map((row) => (
                  <TableRow
                    key={row.assetId}
                    hover
                    className={row.opstat === 255 ? 'critical' : row.opstat === 127 ? 'warning' : ''}
                  >
                    <TableCell>
                      <span
                        className="clickable-cell"
                        onClick={() => onViewDefects?.(row.assetId)}
                      >
                        {row.fleet}
                      </span>
                    </TableCell>
                    <TableCell>
                      <TagsGroup
                        options={row.tags?.map(tag => ({ ...tag, color: getTagColor(tag.color) })) || []}
                        nameField="label"
                        colorField="color"
                      />
                    </TableCell>
                    <TableCell>{row.assetType}</TableCell>
                    <TableCell>{row.manufacturer}</TableCell>
                    <TableCell>
                      <Tooltip title={row.vin}>
                        <span>{row.vin ? `${row.vin.slice(0, 6)}...` : ''}</span>
                      </Tooltip>
                    </TableCell>
                    <TableCell>{formatDate(row.inspDate)}</TableCell>
                    <TableCell>
                      <StatusIcon status={row.opstat === 255 ? 4 : row.opstat === 127 ? 2 : 1} showLabel />
                    </TableCell>
                    <TableCell>
                      <span
                        className="clickable-cell"
                        onClick={() => onViewDefects?.(row.assetId)}
                      >
                        {row.openDefectCount}
                      </span>
                    </TableCell>
                    <TableCell>
                      <Tooltip title={messages.viewDefects || "View Defects"}>
                        <Button
                          size="small"
                          startIcon={<ViewIcon />}
                          onClick={() => onViewDefects?.(row.assetId)}
                        >
                          {messages.view || "View"}
                        </Button>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </div>
        <TablePagination
          component="div"
          count={totalRecords}
          page={page}
          onPageChange={(e, newPage) => setPage(newPage)}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={(e) => {
            setRowsPerPage(parseInt(e.target.value, 10));
            setPage(0);
          }}
          rowsPerPageOptions={[10, 20, 50, 100]}
        />
      </Paper>
    </Box>
  );
};

export default AssetsOpenDefects;
